import { useEffect, useRef } from 'react'

/**
 * Returns a ref that always contains the latest callback without
 * triggering effect dependencies.
 *
 * Useful for passing callbacks to effects, event handlers, or external APIs
 * where you want the latest version but don't want to cause re-initialization.
 *
 * @example
 * ```tsx
 * const onSaveRef = useLatestCallback(onSave)
 *
 * useEffect(() => {
 *   const interval = setInterval(() => {
 *     onSaveRef.current() // Always uses latest onSave
 *   }, 1000)
 *   return () => clearInterval(interval)
 * }, []) // No need to include onSave in dependencies
 * ```
 */
export const useLatestCallback = <T extends (...args: any[]) => any>(
  callback: T | undefined,
) => {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return callbackRef
}
